import { format } from 'date-fns';
import type { FunnelKind, MeetingSlotFormValues } from './types';

const MEETING_GROUP_ID = 6;

export function getEntityTitleFromLabel(label: string) {
  if (!label) {
    return '';
  }
  return label.split(' — #')[0]?.trim() || '';
}

export function getLeadTitle(leadLabel: string) {
  return getEntityTitleFromLabel(leadLabel);
}

export function buildMeetingTitle(
  values: Pick<
    MeetingSlotFormValues,
    'dealLabel' | 'leadLabel' | 'meetingFormat' | 'meetingType' | 'projectTitle'
  >,
  funnelKind: FunnelKind
) {
  const crmEntityName =
    funnelKind === 'lead'
      ? getLeadTitle(values.leadLabel) || '—'
      : funnelKind === 'deal'
        ? getEntityTitleFromLabel(values.dealLabel) || '—'
        : values.projectTitle || '—';

  return `Встреча / ${values.meetingFormat} / ${values.meetingType} / ${crmEntityName}`;
}

export function getMeetingDeadlineIso(
  selectedDate: Date,
  selectedSlot: string
) {
  const parts = selectedSlot.split('-').map((s) => s.trim());
  const startPart = parts[0];
  if (!startPart) {
    return null;
  }

  const [hhStr, mmStr] = startPart.split(':');
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
    return null;
  }

  const deadline = new Date(selectedDate);
  deadline.setHours(hh, mm, 0, 0);
  deadline.setHours(deadline.getHours() - 5);

  return format(deadline, "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

export function buildUfCrmTask(
  values: Pick<
    MeetingSlotFormValues,
    'dealId' | 'leadCrmRef' | 'leadId' | 'projectId'
  >,
  funnelKind: FunnelKind,
  entityTypeId: number | null
) {
  if (funnelKind === 'lead') {
    if (!values.leadId) {
      return [];
    }
    return [values.leadCrmRef ?? `L_${values.leadId}`];
  }

  if (funnelKind === 'deal') {
    if (!values.dealId) {
      return [];
    }
    return [`D_${values.dealId}`];
  }

  if (entityTypeId && values.projectId) {
    return [`T${entityTypeId.toString(16).toLowerCase()}_${values.projectId}`];
  }

  return [];
}

type BuildMeetingTaskPayloadParams = {
  deadlineISO: string;
  responsibleId: number;
  selectedDuration: 30 | 60;
  selectedSlot: string;
  title: string;
  ufCrmTask: string[];
  values: Pick<
    MeetingSlotFormValues,
    'meetingFormat' | 'meetingType' | 'presenterName'
  >;
};

export function buildMeetingTaskPayload({
  deadlineISO,
  responsibleId,
  selectedDuration,
  selectedSlot,
  title,
  ufCrmTask,
  values,
}: BuildMeetingTaskPayloadParams) {
  return {
    TITLE: title,
    DESCRIPTION: [
      `Слот: ${selectedSlot}`,
      `Формат: ${values.meetingFormat}`,
      `Тип: ${values.meetingType}`,
      `CRM: ${ufCrmTask.join(', ')}`,
      `Исполнитель: ${values.presenterName}`,
    ].join('\n'),

    RESPONSIBLE_ID: responsibleId,
    DEADLINE: deadlineISO,
    TIME_ESTIMATE: selectedDuration * 60,
    GROUP_ID: MEETING_GROUP_ID,

    isMeeting: true,
    UF_CRM_TASK: ufCrmTask,
  } as ANY;
}
